import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/components/ui/select";
import {
  Search, Download, ArrowDownToLine, TrendingUp, CheckCircle2,
  SlidersHorizontal, X,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const STATUS_CFG = {
  success:    { color: "#22c55e", bg: "rgba(34,197,94,0.12)",   border: "rgba(34,197,94,0.25)" },
  pending:    { color: "#f59e0b", bg: "rgba(245,158,11,0.12)",  border: "rgba(245,158,11,0.25)" },
  processing: { color: "#06b6d4", bg: "rgba(6,182,212,0.12)",   border: "rgba(6,182,212,0.25)" },
  failed:     { color: "#f43f5e", bg: "rgba(244,63,94,0.12)",   border: "rgba(244,63,94,0.25)" },
};

function InlineBadge({ status }) {
  const key = String(status || "").toLowerCase();
  const cfg = STATUS_CFG[key] || STATUS_CFG.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
    >
      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: cfg.color }} />
      {key.charAt(0).toUpperCase() + key.slice(1)}
    </span>
  );
}

function SummaryCard({ label, value, icon: Icon, accent, delay }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 200, damping: 22 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -4, scale: 1.025 }}
      className="relative overflow-hidden rounded-2xl border border-white/[0.07] p-5 cursor-default"
      style={{
        background: "rgba(13,27,46,0.8)",
        backdropFilter: "blur(20px)",
        boxShadow: hovered ? `0 0 30px 0 ${accent}25` : "0 2px 16px rgba(0,0,0,0.4)",
      }}
    >
      <motion.div
        animate={{ opacity: hovered ? 1 : 0 }}
        className="pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(circle at 30% 40%, ${accent}20 0%, transparent 70%)` }}
      />
      <motion.div
        animate={{ scaleX: hovered ? 1 : 0, opacity: hovered ? 1 : 0 }}
        className="absolute bottom-0 left-0 right-0 h-[2px] origin-left"
        style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }}
      />
      <div className="relative z-10 flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">{label}</p>
          <p className="text-2xl font-black text-white">{value}</p>
        </div>
        <motion.div
          animate={{ rotate: hovered ? 10 : 0, scale: hovered ? 1.15 : 1 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}18`, border: `1px solid ${accent}35` }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function ResellerDeposits() {
  const { t, language } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTx, setSelectedTx] = useState(null);
  const resellerName = "Asia Gaming Network";

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["reseller-deposits"],
    queryFn: async () => [],
  });

  const filteredTransactions = transactions.filter(tx => {
    if (search && !tx.transaction_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && String(tx.status).toLowerCase() !== statusFilter) return false;
    return true;
  });

  const totalSuccess = filteredTransactions.filter(t => String(t.status).toLowerCase() === "success");
  const totalAmount = totalSuccess.reduce((s, t) => s + (t.amount || 0), 0);
  const pending = filteredTransactions.filter(t => String(t.status).toLowerCase() === "pending").length;
  const successRate = filteredTransactions.length > 0 ? ((totalSuccess.length / filteredTransactions.length) * 100).toFixed(1) : "100.0";

  return (
    <ResellerLayout currentPage="ResellerDeposits" resellerName={resellerName}>
      <style>{`
        .dep-bg { min-height: 100vh; background: #060f1c; scroll-behavior: smooth; }
        .glass { background: rgba(13,27,46,0.75); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.065); }
        .row-hover { transition: background 0.18s, transform 0.18s; }
        .row-hover:hover { background: rgba(6,182,212,0.04); transform: translateX(2px); }
        .scrollbar-thin::-webkit-scrollbar { width: 4px; height: 4px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(6,182,212,0.25); border-radius: 99px; }
      `}</style>

      <div className="dep-bg space-y-6 p-1 sm:p-0">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownToLine className="h-4 w-4 text-cyan-400" />
              <span className="text-xs tracking-[0.2em] uppercase text-cyan-400/70 font-semibold">Deposits</span>
            </div>
            <h1 className="text-3xl font-black text-white">{t("deposits")}</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {t("total")}: <span className="text-cyan-400 font-bold">฿{totalAmount.toLocaleString()}</span>
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl glass text-slate-300 hover:text-cyan-400 text-sm font-semibold transition-colors"
          >
            <Download className="h-4 w-4" />
            {t("export")}
          </motion.button>
        </motion.div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryCard label="Total Deposited" value={`฿${totalAmount.toLocaleString()}`} icon={ArrowDownToLine} accent="#06b6d4" delay={0} />
          <SummaryCard label="Success Rate" value={`${successRate}%`} icon={CheckCircle2} accent="#22c55e" delay={0.08} />
          <SummaryCard label="Pending" value={pending} icon={TrendingUp} accent="#f59e0b" delay={0.16} />
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
          className="glass rounded-2xl p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Filters</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                placeholder={`${t("search")}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm text-slate-200 placeholder-slate-500 outline-none bg-[#0a1929] border border-white/[0.07] focus:border-cyan-500/40 transition-colors"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] bg-[#0a1929] border-white/[0.07] text-slate-300 rounded-xl">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1b2e] border-white/[0.07] text-slate-200">
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="success">{t("success")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="failed">{t("failed")}</SelectItem>
              </SelectContent>
            </Select>
            <AnimatePresence>
              {(search || statusFilter !== "all") && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={() => { setSearch(""); setStatusFilter("all"); }}
                  className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  <X className="h-4 w-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28 }}
          className="glass rounded-2xl overflow-hidden"
        >
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {[t("transaction"), t("merchant"), t("amount"), t("status"), t("date")].map((h, i) => (
                    <th
                      key={h}
                      className={`px-5 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em]
                        ${i === 2 ? "text-right" : ""}
                        ${i === 1 ? "hidden sm:table-cell" : ""}
                        ${i === 4 ? "hidden md:table-cell" : ""}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}
                          className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full"
                        />
                        <span className="text-sm text-slate-500">{t("loading")}</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-cyan-500/8 border border-cyan-500/15 flex items-center justify-center">
                          <ArrowDownToLine className="h-7 w-7 text-cyan-500/40" />
                        </div>
                        <p className="text-slate-500 text-sm">{t("noDeposits")}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.slice(0, 50).map((tx, i) => (
                    <motion.tr
                      key={tx.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.018 }}
                      onClick={() => setSelectedTx(tx)}
                      className="row-hover border-b border-white/[0.04] cursor-pointer"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <motion.div
                            whileHover={{ rotate: 8, scale: 1.12 }}
                            className="w-9 h-9 rounded-xl bg-cyan-500/12 border border-cyan-500/20 flex items-center justify-center flex-shrink-0"
                          >
                            <ArrowDownToLine className="h-4 w-4 text-cyan-400" />
                          </motion.div>
                          <div>
                            <p className="font-mono text-sm text-slate-200">{tx.transaction_id?.slice(0, 12)}…</p>
                            <p className="text-xs text-slate-500 sm:hidden">{tx.merchant_code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden sm:table-cell text-sm text-slate-300">{tx.merchant_code || t("nA")}</td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm font-black text-cyan-400">+฿{tx.amount?.toLocaleString()}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <InlineBadge status={tx.status} />
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell text-xs text-slate-500">
                        {tx.created_date ? format(new Date(tx.created_date), language === "th" ? "d MMM, HH:mm" : "MMM d, HH:mm", { locale: language === "th" ? th : enUS }) : t("nA")}
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredTransactions.length > 0 && (
            <div className="px-5 py-3 border-t border-white/[0.05] flex items-center justify-between">
              <span className="text-xs text-slate-500">Showing {Math.min(50, filteredTransactions.length)} of {filteredTransactions.length}</span>
              <div className="w-24 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min((50 / filteredTransactions.length) * 100, 100)}%` }}
                  transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
                />
              </div>
            </div>
          )}
        </motion.div>

        <TransactionDetailPanel transaction={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} />
      </div>
    </ResellerLayout>
  );
}